import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Cloud, Infinity as InfinityIcon, ListChecks, Scale, Server, Smartphone } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Motion";
import { IMAGES } from "@/data/products";
import { Faq } from "./Faq";

const FEATURES = [
  { i: Cloud, t: "Molntjänst", d: "Enkelt att använda var du än är." },
  { i: InfinityIcon, t: "Obegränsat", d: "Kunder, uppdrag, användare och kontor." },
  { i: ListChecks, t: "Checklistor", d: "Färdiga för de vanligaste uppdragstyperna." },
  { i: Scale, t: "Kundkännedom (PTL)", d: "Uppfyll Penningtvättlagen enkelt." },
  { i: Server, t: "Servrar i Sverige", d: "Backup och övervakning." },
  { i: Smartphone, t: "Lättanvänt", d: "Lika enkelt på mobilen som på datorn." },
];

const Feature = ({ f, i }) => (
  <Reveal delay={i * 0.05} className="h-full">
    <div className="h-full rounded-2xl bg-white border border-[#E3E2DA] p-6 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(7,11,46,0.45)]" data-testid={`feature-${i}`}>
      <f.i className="w-6 h-6 text-[#0B14A0]" />
      <h4 className="font-display text-lg font-semibold mt-5 text-[#070B2E]">{f.t}</h4>
      <p className="text-sm text-[#3A3F5C] mt-1.5 leading-relaxed">{f.d}</p>
    </div>
  </Reveal>
);

export const Experience = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} className="py-24 lg:py-36" data-testid="experience-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 lg:grid-rows-[auto_1fr] gap-12 lg:gap-x-16 lg:gap-y-0">
        <div className="lg:col-span-5 lg:col-start-1 lg:row-start-1 self-start">
          <Reveal>
            <div className="clip-notch-sm relative aspect-[4/5] overflow-hidden rounded-[24px] bg-[#070B2E]">
              <motion.img src={IMAGES.building} alt="Modern kontorsbyggnad" style={{ y, scale: 1.25 }} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070B2E]/90 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 sm:bottom-8 sm:left-8 sm:right-8 text-white grid grid-cols-2 gap-4 sm:gap-6">
                <div data-testid="experience-stat-consultant">
                  <p className="font-display text-6xl sm:text-7xl font-bold leading-none tracking-tighter">38+</p>
                  <p className="mt-3 text-[13px] sm:text-sm font-medium text-[#BFF3F6] leading-snug" lang="sv">års erfarenhet som redovisnings­konsult och företagare</p>
                </div>
                <div data-testid="experience-stat-controller">
                  <p className="font-display text-6xl sm:text-7xl font-bold leading-none tracking-tighter">18</p>
                  <p className="mt-3 text-[13px] sm:text-sm font-medium text-[#BFF3F6] leading-snug" lang="sv">års erfarenhet som kvalitets­kontrollant av auktoriserade redovisnings­konsulter</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 lg:row-start-1 lg:row-span-2 self-start flex flex-col">
          <Reveal>
            <Eyebrow>Varför Redoc</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-[-0.03em] leading-[1.02] mt-4">Det ska vara enkelt att skapa hög kvalitet i redovisningsuppdrag.</h2>
            <p className="mt-6 text-base sm:text-lg text-[#3A3F5C] leading-relaxed max-w-2xl">Idén med Redoc är att du som redovisningskonsult enkelt och effektivt får kontroll över dina uppdrag – och samtidigt upprättar den dokumentation och de checklistor du behöver för att kvalitetssäkra uppdragen enligt gällande branschstandarder.</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => <Feature key={f.t} f={f} i={i} />)}
          </div>
        </div>
        <div className="lg:col-span-5 lg:col-start-1 lg:row-start-2 self-start">
          <Faq />
        </div>
      </div>
    </section>
  );
};
